import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, GraduationCap, Info } from 'lucide-react';
import { Step } from '../../types';

interface BlackboardProps {
  steps: Step[];
  currentStep: number;
  onStepClick: (stepIndex: number) => void;
  currentQuestion?: string;
  questionExplanation?: string;
}

export const Blackboard: React.FC<BlackboardProps> = ({
  steps,
  currentStep,
  onStepClick,
  currentQuestion,
  questionExplanation,
}) => {
  const hasExpressions = steps.some(s => s.math && s.math.length > 0);
  const [isQuestionExpanded, setIsQuestionExpanded] = useState(false);
  const [isQuestionClamped, setIsQuestionClamped] = useState(false);
  const questionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsQuestionExpanded(false);
  }, [currentQuestion]);

  useEffect(() => {
    if (!currentQuestion?.trim()) {
      setIsQuestionClamped(false);
      return;
    }

    if (isQuestionExpanded) return;

    const el = questionRef.current;
    if (!el) return;
    setIsQuestionClamped(el.scrollHeight > el.clientHeight + 1);
  }, [currentQuestion, isQuestionExpanded]);

  const shouldShowQuestionToggle = isQuestionExpanded || isQuestionClamped;

  const [isExplanationExpanded, setIsExplanationExpanded] = useState(false);

  useEffect(() => {
    setIsExplanationExpanded(false);
  }, [questionExplanation]);

  return (
    <div 
      className="rounded-xl shadow-lg border border-gray-300 overflow-hidden min-h-[300px] lg:h-full flex flex-col" 
      style={{ background: '#0B0F14' }}
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-700" style={{ background: '#111' }}>
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2">
            <GraduationCap className="w-4 h-4 text-white" />
            Blackboard
          </h3>
          <span className="text-[10px] text-gray-400 flex-shrink-0">All Steps</span>
        </div>

        {!!currentQuestion?.trim() && (
          <div className="mt-2">
            <div className="flex items-start gap-2 min-w-0">
              <span className="flex-shrink-0 text-white font-bold text-sm leading-none">Q</span>
              <div className="min-w-0 flex-1">
                <div
                  ref={questionRef}
                  className="text-[12px] text-white font-semibold leading-snug"
                  style={
                    isQuestionExpanded
                      ? undefined
                      : {
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }
                  }
                >
                  {currentQuestion}
                </div>
                {shouldShowQuestionToggle && (
                  <button
                    type="button"
                    onClick={() => setIsQuestionExpanded((v) => !v)}
                    className="mt-2 inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-[11px] text-blue-200 hover:bg-white/15 hover:text-blue-100"
                  >
                    {isQuestionExpanded ? (
                      <>
                        Show less
                        <ChevronUp className="w-3 h-3" />
                      </>
                    ) : (
                      <>
                        See more
                        <ChevronDown className="w-3 h-3" />
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* About the question */}
        {!!questionExplanation?.trim() && (
          <div className="mt-2">
            <button
              type="button"
              onClick={() => setIsExplanationExpanded((v) => !v)}
              className="w-full flex items-center justify-between gap-2"
            >
              <div className="flex items-center gap-2">
                <Info className="w-3.5 h-3.5 text-white flex-shrink-0" />
                <span className="text-xs font-semibold text-white tracking-wide font-semibold">About this question</span>
              </div>
              {isExplanationExpanded ? (
                <ChevronUp className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
              ) : (
                <ChevronDown className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
              )}
            </button>
            {isExplanationExpanded && (
              <div className="mt-2 pl-5.5">
                <p className="text-[12px] text-gray-300 leading-snug">
                  {questionExplanation}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 flex-1 overflow-y-auto" style={{ background: '#0B0F14' }}>
        {hasExpressions ? (
          <div className="space-y-3">
            {steps.map((step, stepIdx) => (
              (step.math || []).map((expr, exprIdx) => (
                <div
                  key={`${stepIdx}-${exprIdx}`}
                  onClick={() => onStepClick(stepIdx)}
                  className={`font-mono py-2 px-3 rounded cursor-pointer transition-all duration-200 ${
                    stepIdx === currentStep
                      ? 'text-white text-lg bg-white/10'
                      : 'text-gray-500 text-base hover:text-gray-300 hover:bg-white/5'
                  }`}
                  style={{ lineHeight: '1.8' }}
                >
                  {expr}
                </div>
              ))
            ))}
          </div>
        ) : (
          <div className="text-center text-gray-600 text-sm py-8">
            No expressions
          </div>
        )}
      </div>
    </div>
  );
};

export default Blackboard;
